import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from "@/hooks/useToast";

// Types for Job Card Attachments
export interface JobCardAttachment {
  id: string;
  job_card_id: string;
  operation_id?: string;
  file_name: string;
  file_type: string;
  file_url: string;
  file_size?: number;
  upload_category: 'document' | 'image' | 'video' | 'drawing' | 'instruction' | 'other';
  description?: string;
  uploaded_by: string;
  uploaded_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface AttachmentUploadRequest {
  jobCardId: string;
  operationId?: string;
  file: File;
  category: JobCardAttachment['upload_category'];
  description?: string;
  uploadedBy: string;
}

export interface AttachmentUpdateRequest {
  attachmentId: string;
  file_name?: string;
  description?: string;
  category?: JobCardAttachment['upload_category'];
}

type UUID = string;

/**
 * Hook for managing Job Card Attachments
 * Handles file upload, document management, attachment metadata, and integration with Supabase Storage
 */
export const useJobCardAttachments = () => {
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<JobCardAttachment[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const { toast } = useToast();

  /**
   * Fetch attachments for a job card
   */
  const fetchJobCardAttachments = useCallback(async (jobCardId: UUID): Promise<JobCardAttachment[]> => {
    try {
      setLoading(true);
      
      // Get job card operation details
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .select('work_order_operation_id')
        .eq('id', jobCardId)
        .single();
      
      if (jobCardError) throw jobCardError;
      
      // Fetch attachments from operation_attachments table
      const { data, error } = await supabase
        .from('operation_attachments')
        .select('*')
        .eq('operation_id', jobCardData.work_order_operation_id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Map to JobCardAttachment format
      const jobCardAttachments: JobCardAttachment[] = (data || []).map((item: any) => ({
        id: item.id,
        job_card_id: jobCardId,
        operation_id: item.operation_id,
        file_name: item.file_name,
        file_type: item.file_type || 'unknown',
        file_url: item.file_url,
        file_size: undefined, // Not available in current schema
        upload_category: getFileCategory(item.file_type, item.file_name),
        description: undefined, // Not available in current schema
        uploaded_by: item.uploaded_by,
        uploaded_by_name: item.uploaded_by_user?.full_name,
        created_at: item.created_at,
        updated_at: item.created_at, // Using created_at as fallback
      }));
      
      setAttachments(jobCardAttachments);
      return jobCardAttachments;
    } catch (error: any) {
      toast({
        title: 'Error fetching attachments',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Upload file to Supabase Storage and create attachment record
   */
  const uploadAttachment = useCallback(async (request: AttachmentUploadRequest): Promise<JobCardAttachment | null> => {
    try {
      setLoading(true);
      const uploadId = Date.now().toString();
      setUploadProgress(prev => ({ ...prev, [uploadId]: 0 }));
      
      // Generate unique file name
      const fileExt = request.file.name.split('.').pop();
      const fileName = `job_card_${request.jobCardId}_${Date.now()}.${fileExt}`;
      const filePath = `job-cards/${request.jobCardId}/${fileName}`;
      
      // Upload file to Supabase Storage
      setUploadProgress(prev => ({ ...prev, [uploadId]: 25 }));
      const { error: uploadError } = await supabase.storage
        .from('job-card-attachments')
        .upload(filePath, request.file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) throw uploadError;
      
      setUploadProgress(prev => ({ ...prev, [uploadId]: 50 }));
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('job-card-attachments')
        .getPublicUrl(filePath);
      
      setUploadProgress(prev => ({ ...prev, [uploadId]: 75 }));
      
      // Get job card operation details
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .select('work_order_operation_id')
        .eq('id', request.jobCardId)
        .single();
      
      if (jobCardError) throw jobCardError;
      
      // Create attachment record
      const { data: attachmentData, error: attachmentError } = await supabase
        .from('operation_attachments')
        .insert({
          operation_id: jobCardData.work_order_operation_id,
          file_name: request.file.name,
          file_type: request.file.type,
          file_url: urlData.publicUrl,
          uploaded_by: request.uploadedBy,
          created_at: new Date().toISOString()
        })
        .select('*')
        .single();
      
      if (attachmentError) throw attachmentError;
      
      setUploadProgress(prev => ({ ...prev, [uploadId]: 100 }));
      
      // Create job card activity
      await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: request.jobCardId,
          activity_type: 'attachment_uploaded',
          description: `Attachment uploaded: ${request.file.name}`,
          user_id: request.uploadedBy,
          metadata: {
            table_name: 'operation_attachments',
            record_id: attachmentData.id,
            action: 'INSERT',
            old_value: null,
            new_value: {
              id: attachmentData.id,
              job_card_id: request.jobCardId,
              operation_id: jobCardData.work_order_operation_id,
              file_name: request.file.name,
              file_type: request.file.type,
              file_url: urlData.publicUrl,
              uploaded_by: request.uploadedBy,
              created_at: new Date().toISOString()
            },
            category: 'attachment',
            severity: 'info'
          },
          timestamp: new Date().toISOString()
        });
      
      // Create the attachment object to return
      const newAttachment: JobCardAttachment = {
        id: attachmentData.id,
        job_card_id: request.jobCardId,
        operation_id: jobCardData.work_order_operation_id,
        file_name: request.file.name,
        file_type: request.file.type,
        file_url: urlData.publicUrl,
        file_size: request.file.size,
        upload_category: request.category,
        description: request.description,
        uploaded_by: request.uploadedBy,
        uploaded_by_name: undefined,
        created_at: attachmentData.created_at,
        updated_at: attachmentData.created_at,
      };
      
      // Refresh attachments list
      await fetchJobCardAttachments(request.jobCardId);
      
      toast({
        title: 'File Uploaded',
        description: `${request.file.name} uploaded successfully`,
        variant: 'default',
      });
      
      // Clean up upload progress
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[uploadId];
          return newProgress;
        });
      }, 2000);
      
      return newAttachment;
    } catch (error: any) {
      toast({
        title: 'Error uploading file',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchJobCardAttachments, toast]);

  /**
   * Delete an attachment
   */
  const deleteAttachment = useCallback(async (attachmentId: UUID, jobCardId: UUID): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Get attachment details first
      const { data: attachmentData, error: fetchError } = await supabase
        .from('operation_attachments')
        .select('*')
        .eq('id', attachmentId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // Extract file path from URL for deletion from storage
      const url = new URL(attachmentData.file_url);
      const filePath = url.pathname.split('/').slice(-3).join('/'); // Get last 3 parts of path
      
      // Delete file from Supabase Storage
      const { error: storageError } = await supabase.storage
        .from('job-card-attachments')
        .remove([filePath]);
      
      if (storageError) {
        console.warn('Warning: Could not delete file from storage:', storageError.message);
      }
      
      // Delete attachment record
      const { error: deleteError } = await supabase
        .from('operation_attachments')
        .delete()
        .eq('id', attachmentId);
      
      if (deleteError) throw deleteError;
      
      // Create job card activity
      await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: attachmentData.job_card_id,
          activity_type: 'attachment_deleted',
          description: `Attachment deleted: ${attachmentData.file_name}`,
          user_id: (await supabase.auth.getUser()).data.user?.id || 'current-user',
          metadata: {
            table_name: 'operation_attachments',
            record_id: attachmentId,
            action: 'delete',
            field_name: 'file_name',
            old_value: attachmentData.file_name,
            new_value: null,
            category: 'attachment',
            severity: 'info'
          },
          timestamp: new Date().toISOString()
        });
      
      // Refresh attachments list
      await fetchJobCardAttachments(jobCardId);
      
      toast({
        title: 'File Deleted',
        description: `${attachmentData.file_name} deleted successfully`,
        variant: 'default',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error deleting file',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchJobCardAttachments, toast]);

  /**
   * Update attachment metadata
   */
  const updateAttachment = useCallback(async (request: AttachmentUpdateRequest): Promise<boolean> => {
    try {
      setLoading(true);
      
      const updateData: any = {};
      
      if (request.file_name) {
        updateData.file_name = request.file_name;
      }
      
      // Note: description and category are not in current schema, 
      // but keeping for future extensibility
      
      const { error } = await supabase
        .from('operation_attachments')
        .update(updateData)
        .eq('id', request.attachmentId);
      
      if (error) throw error;
      
      toast({
        title: 'Attachment Updated',
        description: 'Attachment metadata updated successfully',
        variant: 'default',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error updating attachment',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Download attachment
   */
  const downloadAttachment = useCallback(async (attachment: JobCardAttachment): Promise<void> => {
    try {
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = attachment.file_url;
      link.download = attachment.file_name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Download Started',
        description: `Downloading ${attachment.file_name}`,
        variant: 'default',
      });
    } catch (error: any) {
      toast({
        title: 'Error downloading file',
        description: error.message,
        variant: 'destructive',
      });
    }
  }, [toast]);

  /**
   * Get attachment statistics for a job card
   */
  const getAttachmentStats = useCallback((jobCardAttachments: JobCardAttachment[]) => {
    const stats = {
      total: jobCardAttachments.length,
      categories: {} as Record<string, number>,
      totalSize: 0,
      recentUploads: 0, // Last 24 hours
    };
    
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    jobCardAttachments.forEach(attachment => {
      // Count by category
      stats.categories[attachment.upload_category] = 
        (stats.categories[attachment.upload_category] || 0) + 1;
      
      // Sum file sizes
      if (attachment.file_size) {
        stats.totalSize += attachment.file_size;
      }
      
      // Count recent uploads
      if (new Date(attachment.created_at) > oneDayAgo) {
        stats.recentUploads++;
      }
    });
    
    return stats;
  }, []);

  return {
    loading,
    attachments,
    uploadProgress,
    fetchJobCardAttachments,
    uploadAttachment,
    deleteAttachment,
    updateAttachment,
    downloadAttachment,
    getAttachmentStats
  };
};

/**
 * Helper function to determine file category based on file type and name
 */
function getFileCategory(fileType: string, fileName: string): JobCardAttachment['upload_category'] {
  const type = fileType?.toLowerCase() || '';
  const name = fileName?.toLowerCase() || '';
  
  if (type.startsWith('image/')) {
    return 'image';
  }
  
  if (type.startsWith('video/')) {
    return 'video';
  }
  
  if (name.includes('drawing') || name.includes('dwg') || type.includes('dwg')) {
    return 'drawing';
  }
  
  if (name.includes('instruction') || name.includes('manual') || name.includes('guide')) {
    return 'instruction';
  }
  
  if (type.includes('pdf') || type.includes('document') || type.includes('text')) {
    return 'document';
  }
  
  return 'other';
}
